// @flow
import BaseModel from 'Stores/BaseModel';
import ListModel from 'Stores/ListModel';
import axios from 'axios';
import { formatDate, fromApiDate, toApiDate } from 'Utils/index';

declare var API_BASE_URL: string;

export default class JobModel extends BaseModel {
  static departments = [
    { value: 1, label: 'Verkauf' },
    { value: 2, label: 'Service' },
    { value: 3, label: 'Werkstatt' },
    { value: 4, label: 'Geschäftsleitung' },
    { value: 5, label: 'Marketing' },
    { value: 6, label: 'Buchhaltung' },
    { value: 7, label: 'Verwaltung' },
    { value: 8, label: 'Assistenz' },
  ];

  static availabilities = [
    { value: 'FROM_NOW', label: 'Ab sofort' },
    { value: 'ONGOING', label: 'Permanent' },
    { value: 'FROM_DATE', label: 'Ab Datum...' },
  ];

  static workingTimes = [
    { value: 'PART_TIME', label: 'Teilzeit' },
    { value: 'FULL_TIME', label: 'Vollzeit' },
    { value: 'MINIJOB', label: 'Minijob' },
    { value: 'FREELANCE', label: 'Freiberuflich' },
  ];

  static limitations = [
    { value: 'LIMITED', label: 'Befristet' },
    { value: 'UNLIMITED', label: 'Unbefristet' },
  ];

  id: number = 0;
  title: string = '';
  description: string = '';
  tasks: string = '';
  benefits: string = '';
  requirements: string = '';
  // must be set when availability is equal to FROM_DATE
  startDate: ?Date = null;
  department: number = JobModel.departments[0].value;
  availability: string = JobModel.availabilities[0].value;
  workingTime: string = JobModel.workingTimes[0].value;
  limitation: string = JobModel.limitations[0].value;
  hidden: boolean = true;

  constructor(data: Object = {}) {
    super();
    this.hydrate(data);
    this.startDate = fromApiDate(data.startDate);
  }

  hasCustomStartdate() {
    return this.availability === 'FROM_DATE';
  }

  getSubline = () => {
    const department = JobModel.departments.find((o) => o.value === this.department);
    const workingTime = JobModel.workingTimes.find((o) => o.value === this.workingTime);

    const subline = [`Status: ${this.hidden ? 'Offline' : 'Online'}`, this.getAvailabilityLabel()];
    department && subline.push(department.label);
    workingTime && subline.push(workingTime.label);
    return subline.join(' | ');
  };

  getAvailabilityLabel = () => {
    if (this.hasCustomStartdate()) {
      return `Ab ${formatDate(this.startDate)}`;
    }
    return JobModel.availabilities.find((o) => o.value === this.availability).label;
  };

  valid = () => {
    this.errors = {};
    if (this.title.length === 0) {
      this.errors.title = 'Bitte geben Sie einen Titel an';
    }

    if (this.tasks.length === 0) {
      this.errors.tasks = 'Bitte geben Sie einen Text an';
    }

    if (this.requirements.length === 0) {
      this.errors.requirements = 'Bitte geben Sie einen Text an';
    }

    if (this.benefits.length === 0) {
      this.errors.benefits = 'Bitte geben Sie einen Text an';
    }

    return Object.keys(this.errors).length === 0;
  };

  toListItem = () => {
    return {
      id: this.id,
      headline: this.title,
      subline: this.getSubline(),
      showIndicator: true,
      hidden: this.hidden,
      model: this,
    };
  };

  //
  // API COMMUICATION
  //

  static getAll = (params: Object = {}) => {
    return axios({
      method: 'get',
      url: `${API_BASE_URL}/jobs/`,
      params,
    }).then((res) => {
      return new ListModel(res.data, JobModel);
    });
  };

  static getById = (id: number) => {
    return axios({
      method: 'get',
      url: `${API_BASE_URL}/jobs/${id}/`,
    }).then((res) => {
      return new JobModel(res.data);
    });
  };

  delete = () => {
    return axios.delete(`${API_BASE_URL}/jobs/${this.id}/`);
  };

  save = () => {
    const job = this.toJSON();

    if (job.id > 0) {
      return axios.put(`${API_BASE_URL}/jobs/${job.id}/`, job).then((res) => {
        return new JobModel(res.data);
      });
    }

    return axios.post(`${API_BASE_URL}/jobs/`, job).then((res) => {
      return new JobModel(res.data);
    });
  };

  toJSON = () => {
    const json = {
      title: this.title,
      description: this.description,
      tasks: this.tasks,
      benefits: this.benefits,
      requirements: this.requirements,
      department: this.department,
      availability: this.availability,
      startDate: this.hasCustomStartdate() ? toApiDate(this.startDate) : null,
      workingTime: this.workingTime,
      limitation: this.limitation,
      hidden: this.hidden,
    };

    if (!this.isNew()) {
      json.id = this.id;
    }

    return json;
  };
}
